import type { SearchOrganizationUsersRequest } from '@compass/client/types/organization_user';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { organizationUserService } from '@/services/api';

const PAGE_SIZE = 100;

export const queryKeys = {
  organizationUsers: {
    all: ['compass', 'organizationUsers'] as const,
    search: (organizationId: string, params?: Partial<SearchOrganizationUsersRequest>) =>
      [...queryKeys.organizationUsers.all, 'search', organizationId, params] as const,
  },
};

/**
 * Hook to search for users within an organization.
 * Automatically fetches all pages using cursor-based pagination so that
 * client-side search/filtering works across the full user list.
 */
export const useOrganizationUsers = (
  organizationId?: string,
  params?: Omit<SearchOrganizationUsersRequest, 'organization_id' | 'start_after_id'>,
) => {
  // Strip limit from params for the query key so that callers passing
  // different limits still share the same cache entry.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { limit: _limit, ...keyParams } = params ?? {};

  const query = useInfiniteQuery({
    queryKey: queryKeys.organizationUsers.search(organizationId || '', keyParams),
    queryFn: async ({ pageParam }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      return organizationUserService.searchOrganizationUsers({
        organization_id: organizationId,
        ...params,
        limit: PAGE_SIZE,
        start_after_id: pageParam,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => {
      // Use the server-provided cursor rather than inferring "done" from page
      // length. The backend fetches PAGE_SIZE memberships then filters out
      // inactive/deleted/no-role users, so a page can return fewer than
      // PAGE_SIZE users while more pages still remain. next_start_after_id is
      // derived from the last membership scanned and is empty on the last page.
      return lastPage?.next_start_after_id || undefined;
    },
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Eagerly fetch all remaining pages so the full user list is available
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isError } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isError]);

  // Flatten all users from all pages into a single array
  const users = useMemo(
    () => query.data?.pages.flatMap(page => page?.users || []) || [],
    [query.data?.pages],
  );

  return {
    data: { users },
    isLoading: query.isLoading,
    isFetchingMore: query.isFetchingNextPage,
    allLoaded: !hasNextPage && !isFetchingNextPage && !query.isLoading,
    error: query.error,
  };
};
